import React from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';

const SearchHeader = ({ 
  title,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Αναζήτηση...',
  actions = [],
  subtitle = null,
  showSearch = true,
  statusFilter = null,
  onStatusFilterChange = null,
  statusOptions = [],
  statusLabel = 'Κατάσταση',
  extraFilters = null,
  sortField = null,
  sortDirection = null,
  onSortChange = null,
  sortOptions = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ 
      mb: 3,
      p: 3,
      backgroundColor: 'background.paper',
      borderRadius: 2,
      boxShadow: 1
    }}>
      {/* Title and subtitle */}
      {(title || subtitle) && (
        <Box sx={{ mb: showSearch || actions.length > 0 ? 2 : 0 }}>
          {title && (
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                mb: subtitle ? 0.5 : 0
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography 
              variant="body2" 
              sx={{ color: 'text.secondary' }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {/* Search and actions row */}
      {(showSearch || actions.length > 0) && (
        <Stack 
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          {/* Search field - full width on mobile, in row with filters on desktop */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            sx={{ flexGrow: 1 }}
          >
            {/* Search bar - own row on mobile */}
            {showSearch && (
              <TextField
                size="small"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                sx={{ 
                  flexGrow: 1,
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '100%', sm: '300px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            )}
            
            {/* Filters row - below search on mobile, next to search on desktop */}
            <Box
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              {extraFilters}

              {/* Status Filter - smaller field, should fit next to sort on mobile if space allows */}
              {statusFilter !== null && onStatusFilterChange && (
                <FormControl 
                  size="small" 
                  sx={{ 
                    minWidth: { xs: 120, sm: 120 },
                    flex: { xs: '0 1 120px', sm: '0 0 auto' },
                    width: { xs: 'auto', sm: 'auto' }
                  }}
                >
                  <InputLabel>{statusLabel}</InputLabel>
                  <Select
                    value={statusFilter || 'all'}
                    onChange={(e) => onStatusFilterChange(e.target.value)}
                    label={statusLabel}
                  >
                    {(statusOptions.length > 0 ? statusOptions : [
                      { value: 'all', label: 'Όλοι' },
                      { value: 'active', label: 'Ενεργοί' },
                      { value: 'inactive', label: 'Ανενεργοί' }
                    ]).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Sort Selector - larger field */}
              {sortOptions.length > 0 && onSortChange && sortField !== null && (
                <FormControl 
                  size="small" 
                  sx={{ 
                    minWidth: { xs: 150, sm: 150 },
                    flex: { xs: '1 1 150px', sm: '0 0 auto' },
                    width: { xs: 'auto', sm: 'auto' }
                  }}
                >
                  <InputLabel>Ταξινόμηση</InputLabel>
                  <Select
                    value={`${sortField}_${sortDirection}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Τελευταίο _ χωρίζει πεδίο (π.χ. created_at, client_name) από asc/desc — όχι split σε κάθε _
                      const last = v.lastIndexOf('_');
                      const direction = v.slice(last + 1);
                      const field = v.slice(0, last);
                      if (direction === 'asc' || direction === 'desc') {
                        onSortChange(field, direction);
                      }
                    }}
                    label="Ταξινόμηση"
                  >
                    {sortOptions.flatMap((option) => [
                      <MenuItem key={`${option.value}_asc`} value={`${option.value}_asc`}>
                        {option.label} ↑
                      </MenuItem>,
                      <MenuItem key={`${option.value}_desc`} value={`${option.value}_desc`}>
                        {option.label} ↓
                      </MenuItem>
                    ])}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Stack>

          {/* Actions */}
          {actions.length > 0 && (
            <Stack 
              direction="row" 
              spacing={1}
              sx={{ 
                flexShrink: 0,
                justifyContent: { xs: 'flex-end', sm: 'flex-start' }
              }}
            >
              {actions.map((action, index) => (
                <Box key={index}>
                  {action}
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default SearchHeader;